import { useState } from "react";
import { Reveal } from "./Reveal";
import { CONTACT, whatsappLink, SERVICES } from "../../config";
import { createLead, track } from "../../lib/api";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { CalendarClock, MessageCircle, Mail, Loader2 } from "lucide-react";

const empty = { name: "", email: "", phone: "", company: "", service: "", budget: "", message: "" };

export const Contact = ({ onBook }) => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Escribe al menos tu nombre y email.");
      return;
    }
    setLoading(true);
    try {
      await createLead({ ...form, source: "contact_form" });
      track("lead_submit", form.service || null, { budget: form.budget });
      toast.success("¡Recibido! Te contacto muy pronto.");
      setForm(empty);
    } catch (err) {
      toast.error("Algo falló al enviar. Intenta por WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contacto" data-testid="contact" className="relative z-10 py-24 sm:py-32">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom,rgba(112,0,255,0.12),transparent_60%)] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-5">
          <p className="overline mb-3">Agenda</p>
          <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
            ¿Listo para tu próximo proyecto?
          </h2>
          <p className="mt-6 text-zinc-400 leading-relaxed max-w-md">
            Agenda una llamada corta de diagnóstico. Si prefieres, escríbeme por WhatsApp o
            déjame tus datos y yo te contacto.
          </p>

          <div className="mt-10 space-y-3">
            <button
              onClick={onBook}
              data-testid="contact-book-btn"
              className="w-full flex items-center gap-4 rounded-2xl bg-[var(--magenta)] text-white px-6 py-5 hover-lift hover:magenta-glow text-left"
            >
              <CalendarClock size={22} />
              <span>
                <span className="block font-semibold">Agendar llamada</span>
                <span className="block text-sm text-white/80">Calendario en vivo</span>
              </span>
            </button>

            <a
              href={whatsappLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-btn"
              onClick={() => track("whatsapp_click", null, { location: "contact" })}
              className="w-full flex items-center gap-4 rounded-2xl glass px-6 py-5 hover-lift hover:border-white/25 transition-colors duration-300 text-left"
            >
              <MessageCircle size={22} className="text-emerald-400" />
              <span>
                <span className="block font-semibold">WhatsApp</span>
                <span className="block text-sm text-zinc-400">Respuesta directa</span>
              </span>
            </a>

            <a
              href={`mailto:${CONTACT.email}`}
              data-testid="contact-email-btn"
              className="w-full flex items-center gap-4 rounded-2xl glass px-6 py-5 hover-lift hover:border-white/25 transition-colors duration-300 text-left"
            >
              <Mail size={22} className="text-zinc-300" />
              <span>
                <span className="block font-semibold">Email</span>
                <span className="block text-sm text-zinc-400">{CONTACT.email}</span>
              </span>
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-7">
          <form
            onSubmit={submit}
            data-testid="lead-form"
            className="glass rounded-3xl p-6 sm:p-8"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Nombre *">
                <Input data-testid="lead-name" value={form.name} onChange={set("name")} placeholder="Tu nombre" className="bg-white/5 border-white/10 h-12" />
              </Field>
              <Field label="Email *">
                <Input data-testid="lead-email" type="email" value={form.email} onChange={set("email")} placeholder="tu@correo.com" className="bg-white/5 border-white/10 h-12" />
              </Field>
              <Field label="Teléfono / WhatsApp">
                <Input data-testid="lead-phone" value={form.phone} onChange={set("phone")} placeholder="Opcional" className="bg-white/5 border-white/10 h-12" />
              </Field>
              <Field label="Empresa">
                <Input data-testid="lead-company" value={form.company} onChange={set("company")} placeholder="Opcional" className="bg-white/5 border-white/10 h-12" />
              </Field>
              <Field label="¿Qué necesitas?">
                <select
                  data-testid="lead-service"
                  value={form.service}
                  onChange={set("service")}
                  className="w-full h-12 rounded-md bg-white/5 border border-white/10 px-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[var(--magenta)]"
                >
                  <option value="" className="bg-black">Selecciona…</option>
                  {SERVICES.map((s) => (
                    <option key={s.id} value={s.title} className="bg-black">{s.title}</option>
                  ))}
                  <option value="No estoy seguro" className="bg-black">No estoy seguro</option>
                </select>
              </Field>
              <Field label="Presupuesto aprox.">
                <select
                  data-testid="lead-budget"
                  value={form.budget}
                  onChange={set("budget")}
                  className="w-full h-12 rounded-md bg-white/5 border border-white/10 px-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[var(--magenta)]"
                >
                  <option value="" className="bg-black">Selecciona…</option>
                  <option value="$400 - $1,000" className="bg-black">$400 – $1,000 USD</option>
                  <option value="$1,000 - $3,000" className="bg-black">$1,000 – $3,000 USD</option>
                  <option value="$3,000 - $8,000" className="bg-black">$3,000 – $8,000 USD</option>
                  <option value="$8,000+" className="bg-black">$8,000+ USD</option>
                  <option value="Por definir" className="bg-black">Por definir</option>
                </select>
              </Field>
            </div>
            <div className="mt-4">
              <Field label="Cuéntame de tu proyecto">
                <Textarea data-testid="lead-message" value={form.message} onChange={set("message")} placeholder="Qué vendes, a quién y qué quieres lograr…" rows={4} className="bg-white/5 border-white/10 resize-none" />
              </Field>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="lead-submit-btn"
              className="mt-6 w-full rounded-full bg-[var(--magenta)] text-white font-semibold px-6 py-4 hover-lift hover:magenta-glow disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 size={18} className="animate-spin" />}
              {loading ? "Enviando…" : "Enviar solicitud"}
            </button>
            <p className="mt-3 text-center text-xs text-zinc-500">
              Sin spam. Te respondo personalmente.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs text-zinc-400 mb-1.5">{label}</span>
    {children}
  </label>
);
